import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from './authConfig';
import './AuditLogs.css';

const AuditLogs = () => {
    const { instance, accounts } = useMsal();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                if (accounts.length === 0) return;
                const tokenResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0]
                });
                const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/audit-logs`, {
                    headers: { 'x-auth-token': tokenResponse.accessToken }
                }); if (!response.ok) {
                    throw new Error('Failed to fetch audit logs');
                }

                const data = await response.json();
                setLogs(data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching audit logs:", err);
                setError("Failed to load audit history.");
                setLoading(false);
            }
        };

        fetchLogs();
    }, [instance, accounts]);

    const formatActionType = (type) => {
        switch (type) {
            case 'CREATE_USER': return <span className="action-badge create">Create</span>;
            case 'UPDATE_USER': return <span className="action-badge update">Update</span>;
            case 'TERMINATE_USER': return <span className="action-badge terminate">Terminate</span>;
            default: return <span className="action-badge default">{type}</span>;
        }
    };

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        }).format(date);
    };

    const isSensitiveAuditKey = (key) => {
        const normalizedKey = String(key).replace(/[^a-z0-9]/gi, '').toLowerCase();
        return [
            'password',
            'passcode',
            'pin',
            'secret',
            'token',
            'apikey',
            'accountnumber',
            'accountpin',
            'temporarypassword',
            'ssn'
        ].some(part => normalizedKey.includes(part));
    };

    const getReadableAuditValue = (value) => {
        if (value === null || value === undefined) return 'Cleared';

        if (typeof value === 'object') {
            if (value.label !== undefined) return value.label || 'Cleared';
            if (value.email !== undefined) return value.email;
            if (value.phone !== undefined) return value.phone;
            if (value.date !== undefined) return value.date;
            if (value.labels && Array.isArray(value.labels)) {
                return value.labels.length > 0 ? value.labels.join(', ') : 'Cleared';
            }
            return JSON.stringify(value);
        }

        return String(value);
    };

    const formatAuditDetails = (detailsJson) => {
        if (!detailsJson) return <span className="text-gray-600 italic">No details</span>;
        try {
            const data = JSON.parse(detailsJson);
            const changes = [];

            // Helper mapping for ugly Monday.com column IDs to readable names
            const colMap = {
                "phone____1": "Phone", "email_address": "Email", "dropdown1": "SharePoint Sites",
                "dropdown3": "Shared Mailboxes", "date1": "Start Date", "date7": "Termination Date",
                "status7": "Status", "location": "Location", "title": "Job Title",
                "temporary_password": "Temp Password", "color_mkv5qk8s": "License Type",
                "text8": "Display Name", "person": "User Name"
            };

            for (const [key, value] of Object.entries(data)) {
                // Ignore internal technical columns
                if (key.includes('subitems')) continue;

                const readableKey = colMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                const readableValue = isSensitiveAuditKey(key) ? 'Redacted' : getReadableAuditValue(value);

                changes.push(
                    <div key={key} className="detail-item">
                        <span className="detail-key">{readableKey}:</span>
                        <span className="detail-value">{readableValue}</span>
                    </div>
                );
            }

            if (changes.length === 0) return <span className="text-gray-500 italic">No modifications</span>;
            return <div className="details-list">{changes}</div>;

        } catch (e) {
            return <div className="text-red-400 text-xs truncate max-w-xs">{String(detailsJson)}</div>;
        }
    };

    const filteredLogs = logs.filter(log => {
        const term = searchTerm.toLowerCase();
        return (
            (log.user_name || '').toLowerCase().includes(term) ||
            (log.user_email || '').toLowerCase().includes(term) ||
            (log.target_name || '').toLowerCase().includes(term) ||
            (log.action_type || '').toLowerCase().includes(term)
        );
    });

    if (loading) return <div className="audit-loading">Loading audit ledger...</div>;
    if (error) return <div className="audit-error">{error}</div>;

    return (
        <div className="audit-logs-container">
            <div className="audit-header">
                <h2>System Audit Logs</h2>
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="Search users, actions, or targets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="audit-table-wrapper">
                <table className="audit-table">
                    <thead>
                        <tr>
                            <th>Date & Time</th>
                            <th>Action By</th>
                            <th>Action</th>
                            <th>Target Record</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLogs.length > 0 ? (
                            filteredLogs.map(log => (
                                <tr key={log.id}>
                                    <td className="col-date">{formatDate(log.timestamp)}</td>
                                    <td className="col-actor">
                                        <div className="actor-name">{log.user_name}</div>
                                        <div className="actor-email">{log.user_email}</div>
                                    </td>
                                    <td className="col-action">{formatActionType(log.action_type)}</td>
                                    <td className="col-target">{log.target_name}</td>
                                    <td className="col-details">
                                        {formatAuditDetails(log.details)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" style={{ padding: '6rem 0', textAlign: 'center', borderBottom: 'none' }}>
                                    <div className="no-logs-text">
                                        No audit records found.
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AuditLogs;
